import { useEffect, useState } from "react";
import { getRequests } from "../api";
import RequestDetail from "./RequestDetail";

const STATUSES = ["", "pending", "verified", "approved", "rejected"];
const DOMAINS  = ["", "general", "medical", "financial", "legal", "hr", "other"];
const RISKS    = ["", "low", "medium", "high", "critical"];

function fmt(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function RequestList({
  refresh,
  initialFilters  = {},
  initialSelected = null,
  // User/manager role props
  lockedEmail   = null,   // when set, requester_email filter is locked and hidden
  pageTitle     = "AI Requests",
  pageSubtitle  = "All submitted AI use-case requests and their governance status.",
  // Manager toggle props
  isManager     = false,
  managerEmail  = null,   // the manager's own email — used for "team" view filter
  teamName      = null,   // display label for the team
  // Auth context forwarded to RequestDetail
  auth          = null,
}) {
  // "me" = locked to requester_email, "team" = locked to manager_email
  const [view, setView]       = useState("me");
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(initialSelected);
  const [filters,  setFilters]  = useState({
    status:     initialFilters.status     || "",
    domain:     initialFilters.domain     || "",
    risk_level: initialFilters.risk_level || "",
  });

  const load = () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

    if (isManager && view === "team") {
      // Show all requests this manager approved / manages
      if (managerEmail) params.manager_email = managerEmail;
    } else if (lockedEmail) {
      // Regular user or manager looking at own requests
      params.requester_email = lockedEmail;
    }

    getRequests(params)
      .then(setRequests)
      .finally(() => setLoading(false));
  };

  useEffect(load, [filters, refresh, lockedEmail, view]); // eslint-disable-line

  // Reset view when page props change
  useEffect(() => { setView("me"); }, [lockedEmail]);

  if (selected) {
    return (
      <RequestDetail
        requestId={selected}
        onBack={() => { setSelected(null); load(); }}
        auth={auth}
      />
    );
  }

  const showRequesterCol = view === "team" || !lockedEmail;

  return (
    <>
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h1>{pageTitle}</h1>
            <p>
              {view === "team" && isManager
                ? `Showing all requests submitted by your ${teamName || "team"}'s reportees.`
                : pageSubtitle}
            </p>
          </div>

          {/* Manager toggle */}
          {isManager && (
            <div style={{ display: "flex", background: "var(--border)", borderRadius: 8, padding: 3, gap: 3 }}>
              {[
                ["me",   "My Requests"],
                ["team", "Reportees Requests"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  style={{
                    padding: "7px 16px",
                    borderRadius: 6, border: "none", cursor: "pointer",
                    fontWeight: 600, fontSize: 13, fontFamily: "inherit",
                    background: view === v ? "var(--surface)" : "transparent",
                    color: view === v ? "var(--primary)" : "var(--muted)",
                    boxShadow: view === v ? "var(--shadow)" : "none",
                    transition: ".15s",
                  }}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="page-body">
        <div className="header-row">
          <div className="filters">
            <select value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All Statuses"}
                </option>
              ))}
            </select>
            <select value={filters.domain}
              onChange={(e) => setFilters((f) => ({ ...f, domain: e.target.value }))}>
              {DOMAINS.map((d) => (
                <option key={d} value={d}>
                  {d ? d.charAt(0).toUpperCase() + d.slice(1) : "All Domains"}
                </option>
              ))}
            </select>
            <select value={filters.risk_level}
              onChange={(e) => setFilters((f) => ({ ...f, risk_level: e.target.value }))}>
              {RISKS.map((r) => (
                <option key={r} value={r}>
                  {r ? r.charAt(0).toUpperCase() + r.slice(1) : "All Risk Levels"}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* View-mode notice */}
        {view === "me" && lockedEmail && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 14px", marginBottom: 14,
            background: "var(--primary-light, #eef0ff)",
            border: "1px solid var(--primary)",
            borderRadius: 7, fontSize: 13, color: "var(--primary)",
          }}>
            <span>🔒</span>
            <span>Showing only your requests (<strong>{lockedEmail}</strong>)</span>
          </div>
        )}
        {view === "team" && isManager && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 14px", marginBottom: 14,
            background: "#fffbeb",
            border: "1px solid #d97706",
            borderRadius: 7, fontSize: 13, color: "#92400e",
          }}>
            <span>👥</span>
            <span>Showing all requests where <strong>{managerEmail}</strong> is the listed manager.</span>
          </div>
        )}

        <div className="card">
          <div className="table-wrap">
            {loading ? (
              <div className="empty">Loading…</div>
            ) : requests.length === 0 ? (
              <div className="empty">
                {view === "team"
                  ? "No requests found for your team yet."
                  : lockedEmail
                    ? "You haven't submitted any requests yet."
                    : "No requests found."}
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Title</th>
                    {showRequesterCol && <th>Requester</th>}
                    <th>Domain</th>
                    <th>Risk</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id}>
                      <td className="text-muted">{r.id}</td>
                      <td>
                        <div><strong>{r.title}</strong></div>
                        {r.status === "approved" && (r.approved_model || r.approver_name || r.reviewer) && (
                          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>
                            {r.approved_model?.name && (
                              <span>Model: <strong style={{ color: "var(--text)" }}>{r.approved_model.name}</strong> · </span>
                            )}
                            <span>By: {r.approver_name || r.reviewer}{r.approver_role ? ` (${r.approver_role})` : ""}</span>
                          </div>
                        )}
                      </td>
                      {showRequesterCol && (
                        <td>
                          <div style={{ fontSize: 13 }}>{r.requester}</div>
                          <div className="text-muted">{r.requester_email}</div>
                        </td>
                      )}
                      <td className="text-muted" style={{ textTransform: "capitalize" }}>
                        {r.domain}
                      </td>
                      <td>
                        <span className={`badge badge-${r.risk_level}`}>{r.risk_level}</span>
                      </td>
                      <td className="risk-score">{r.risk_score}</td>
                      <td>
                        <span className={`badge badge-${r.status}`}>{r.status}</span>
                      </td>
                      <td className="text-muted" style={{ whiteSpace: "nowrap" }}>
                        {fmt(r.created_at)}
                      </td>
                      <td>
                        <button className="btn btn-outline btn-sm"
                          onClick={() => setSelected(r.id)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
